import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Phone, Mail, MapPin, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Computer / Laptop Repair",
  "Wi-Fi & Home Network Setup",
  "Smart TV & Device Setup",
  "Virus / Malware Removal",
  "Phone & Tablet Support",
  "Small Business IT",
  "Something else",
];

export default function EnquiryForm() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: SERVICE_OPTIONS[0],
    is_senior: false,
    is_business: false,
    message: "",
    website: "", // honeypot — must stay empty; real users never see this
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [renderedAt] = useState(() => Date.now());

  const update = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, phone and message.");
      return;
    }
    setSubmitting(true);
    try {
      const elapsedMs = Date.now() - renderedAt;
      const payload = {
        ...form,
        email: form.email.trim() || null,
        elapsed_ms: elapsedMs,
      };
      await axios.post(`${API}/enquiries`, payload, { timeout: 15000 });
      setSubmitted(true);
      toast.success("Enquiry sent! Daz will get back to you soon.");
      setForm({
        name: "",
        phone: "",
        email: "",
        service: SERVICE_OPTIONS[0],
        is_senior: false,
        is_business: false,
        message: "",
        website: "",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Couldn't send your enquiry. Please call instead.";
      toast.error(typeof msg === "string" ? msg : "Couldn't send enquiry.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="enquiry" data-testid="enquiry-section" className="py-20 sm:py-24">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Info column */}
          <div className="lg:col-span-5">
            <p className="text-sm uppercase tracking-[0.18em] font-semibold text-[#D97706]">
              Get in touch
            </p>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">
              Tell me what's going on — I'll be in touch.
            </h2>
            <p className="mt-5 text-lg text-slate-700 leading-relaxed">
              The quickest way to reach me is a quick phone call. Prefer to type
              it out? Use the form and I'll get back to you, usually the same
              day.
            </p>

            <ul className="mt-8 space-y-4">
              <li className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#1E56B8]/10 flex items-center justify-center shrink-0">
                  <Phone className="w-6 h-6 text-[#1E56B8]" />
                </div>
                <div>
                  <div className="text-sm text-slate-500 font-medium">Call or text</div>
                  <a
                    href="tel:0409049094"
                    data-testid="contact-phone-link"
                    className="text-xl font-semibold text-slate-900 hover:text-[#1E56B8]"
                  >
                    0409 049 094
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#1E56B8]/10 flex items-center justify-center shrink-0">
                  <Mail className="w-6 h-6 text-[#1E56B8]" />
                </div>
                <div>
                  <div className="text-sm text-slate-500 font-medium">Or send a message</div>
                  <span className="text-xl font-semibold text-slate-900">
                    Use the form
                  </span>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#1E56B8]/10 flex items-center justify-center shrink-0">
                  <MapPin className="w-6 h-6 text-[#1E56B8]" />
                </div>
                <div>
                  <div className="text-sm text-slate-500 font-medium">Based in</div>
                  <span className="text-xl font-semibold text-slate-900">
                    Currambine, WA 6028
                  </span>
                </div>
              </li>
            </ul>
          </div>

          {/* Form column */}
          <div className="lg:col-span-7">
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 sm:p-10">
              {submitted ? (
                <div data-testid="enquiry-success" className="text-center py-10">
                  <div className="mx-auto w-16 h-16 rounded-full bg-green-50 flex items-center justify-center">
                    <CheckCircle2 className="w-9 h-9 text-[#16A34A]" />
                  </div>
                  <h3 className="mt-5 text-2xl font-bold text-slate-900 font-[Outfit]">
                    Thanks — message received!
                  </h3>
                  <p className="mt-3 text-base text-slate-700 max-w-md mx-auto">
                    Daz will get back to you as soon as possible. If it's urgent,
                    please call <a className="text-[#1E56B8] font-semibold" href="tel:0409049094">0409 049 094</a>.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    data-testid="enquiry-send-another"
                    className="mt-6 inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800"
                  >
                    Send another
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} noValidate className="space-y-5" data-testid="enquiry-form">
                  {/* Honeypot field — hidden from real users, bots will fill it in. */}
                  <div
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      left: "-10000px",
                      top: "auto",
                      width: "1px",
                      height: "1px",
                      overflow: "hidden",
                    }}
                  >
                    <label htmlFor="f-website">Website (leave blank)</label>
                    <input
                      id="f-website"
                      type="text"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.website}
                      onChange={update("website")}
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Your name *" htmlFor="f-name">
                      <input
                        id="f-name"
                        data-testid="enquiry-name-input"
                        type="text"
                        value={form.name}
                        onChange={update("name")}
                        className="h-14 w-full rounded-xl border border-slate-300 bg-white px-4 text-lg text-slate-900 focus:border-[#1E56B8] focus:ring-0 outline-none"
                        placeholder="e.g. Pat Smith"
                      />
                    </Field>
                    <Field label="Phone number *" htmlFor="f-phone">
                      <input
                        id="f-phone"
                        data-testid="enquiry-phone-input"
                        type="tel"
                        inputMode="tel"
                        value={form.phone}
                        onChange={update("phone")}
                        className="h-14 w-full rounded-xl border border-slate-300 bg-white px-4 text-lg text-slate-900 focus:border-[#1E56B8] focus:ring-0 outline-none"
                        placeholder="04XX XXX XXX"
                      />
                    </Field>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Email (optional)" htmlFor="f-email">
                      <input
                        id="f-email"
                        data-testid="enquiry-email-input"
                        type="email"
                        value={form.email}
                        onChange={update("email")}
                        className="h-14 w-full rounded-xl border border-slate-300 bg-white px-4 text-lg text-slate-900 focus:border-[#1E56B8] focus:ring-0 outline-none"
                        placeholder="you@example.com"
                      />
                    </Field>
                    <Field label="What do you need help with?" htmlFor="f-service">
                      <select
                        id="f-service"
                        data-testid="enquiry-service-select"
                        value={form.service}
                        onChange={update("service")}
                        className="h-14 w-full rounded-xl border border-slate-300 bg-white px-4 text-lg text-slate-900 focus:border-[#1E56B8] focus:ring-0 outline-none"
                      >
                        {SERVICE_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>

                  <Field label="Tell me a bit more *" htmlFor="f-message">
                    <textarea
                      id="f-message"
                      data-testid="enquiry-message-input"
                      value={form.message}
                      onChange={update("message")}
                      rows={5}
                      className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-lg text-slate-900 focus:border-[#1E56B8] focus:ring-0 outline-none resize-y"
                      placeholder="What's going on with your tech? When suits for a visit?"
                    />
                  </Field>

                  <div className="space-y-3">
                    <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="enquiry-senior-label">
                      <input
                        type="checkbox"
                        checked={form.is_senior}
                        onChange={update("is_senior")}
                        disabled={form.is_business}
                        data-testid="enquiry-senior-checkbox"
                        className="mt-1 w-5 h-5 rounded border-slate-400 text-[#1E56B8] focus:ring-[#1E56B8] disabled:opacity-50"
                      />
                      <span className="text-base text-slate-700">
                        I&apos;m an eligible senior — free service applies (home use only).
                      </span>
                    </label>

                    <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="enquiry-business-label">
                      <input
                        type="checkbox"
                        checked={form.is_business}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setForm((f) => ({
                            ...f,
                            is_business: checked,
                            // Business excludes seniors free rate
                            is_senior: checked ? false : f.is_senior,
                          }));
                        }}
                        data-testid="enquiry-business-checkbox"
                        className="mt-1 w-5 h-5 rounded border-slate-400 text-[#1E56B8] focus:ring-[#1E56B8]"
                      />
                      <span className="text-base text-slate-700">
                        This is a <span className="font-semibold">business enquiry</span> — charged at $2/minute.
                      </span>
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="enquiry-submit-button"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 h-14 px-8 rounded-xl bg-[#1E56B8] hover:bg-[#2E6BD9] text-white font-semibold text-lg shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Send Enquiry
                      </>
                    )}
                  </button>

                  <p className="text-sm text-slate-500">
                    By submitting you agree to be contacted about your enquiry.
                    Your details are never shared.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <label htmlFor={htmlFor} className="block">
      <span className="block mb-2 text-base font-medium text-slate-800">{label}</span>
      {children}
    </label>
  );
}
